import { useEffect, useState } from 'react';
import getReferrerPathname from '../utils/getReferrerPathname';
import { VALID_BACK_LINK_REFERRER_PATTERNS } from '../constants/LeadManagementConstants';

/** Single session key — the referring property is one listing regardless of the current multi-listing selection. */
export const REFERRING_LISTING_ALIAS_STORAGE_KEY = 'leadManagementReferringListingAlias';

/** Pulls the listing alias out of the first referrer pattern that matches (capture group 1), or null. */
const extractReferringAlias = (pathname: string): string | null => {
  for (const pattern of VALID_BACK_LINK_REFERRER_PATTERNS) {
    const alias = pattern.exec(pathname)?.[1];
    if (alias) return alias;
  }
  return null;
};

/**
 * Returns the listing alias of the property dashboard the user navigated from, or null if they
 * didn't arrive from a matching property surface. Unlike reading page state, this is stable across
 * filter/selection changes — it reflects where the user came from, not what's currently selected.
 * Uses the referrer on fresh navigation and falls back to a sessionStorage cache when the referrer
 * is empty (e.g., on reload).
 */
const useReferringListingAlias = (): string | null => {
  const [referringListingAlias, setReferringListingAlias] = useState<string | null>(null);

  useEffect(() => {
    const referrerPathname = getReferrerPathname();

    try {
      // Fresh navigation: derive from the referrer and cache it (or clear a stale cache on a non-match).
      if (referrerPathname) {
        const alias = extractReferringAlias(referrerPathname);
        if (alias) {
          sessionStorage.setItem(REFERRING_LISTING_ALIAS_STORAGE_KEY, alias);
        } else {
          sessionStorage.removeItem(REFERRING_LISTING_ALIAS_STORAGE_KEY);
        }
        setReferringListingAlias(alias);
        return;
      }
      // Empty referrer (likely a reload): fall back to the cached alias.
      setReferringListingAlias(sessionStorage.getItem(REFERRING_LISTING_ALIAS_STORAGE_KEY));
    } catch {
      // If storage is unavailable, fall back to the referrer-derived result.
      setReferringListingAlias(extractReferringAlias(referrerPathname));
    }
  }, []);

  return referringListingAlias;
};

export default useReferringListingAlias;
